<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { routes } from '@app/router/routes'
import { config } from '@core/config'
import {
  useDepositRequestStatusCountsQuery,
  useRefundStatusCountsQuery,
  useWithdrawRequestStatusCountsQuery,
} from '@modules/treasury'
import { useKycPendingStatusCountsQuery } from '@modules/investors/api/queries/useKycPendingStatusCountsQuery'
import { useKycExpiredStatusCountsQuery } from '@modules/investors/api/queries/useKycExpiredStatusCountsQuery'
import { useBankVerificationStatusCountsQuery } from '@modules/investors/api/queries/useBankVerificationStatusCountsQuery'
import { useVaspOnboardingStatusCountsQuery } from '@modules/investors/api/queries/useVaspOnboardingStatusCountsQuery'
import { useRejectedInvestorsStatusCountsQuery } from '@modules/investors/api/queries/useRejectedInvestorsStatusCountsQuery'
import { useOnboardingReviewStatusCountsQuery } from '@modules/investors/api/queries/useOnboardingReviewStatusCountsQuery'
import { usePropertyStatusCountsQuery } from '@modules/properties/api/queries/usePropertyStatusCountsQuery'
import {
  useRentReceiptCountsQuery,
  useRentalDistributionCountsQuery,
} from '@modules/rentalDistributions'
import { useListingRequestNavGroups } from '@modules/listingRequests'
import { useOfferingStatusCountsQuery } from '@modules/offerings'
import { usePropertyOwnerKycReviewCountQuery } from '@modules/propertyOwners/accounts/api/queries/usePropertyOwnerKycReviewCountQuery'
import { usePropertyOwnerOnboardingReviewCountQuery } from '@modules/propertyOwners/accounts/api/queries/usePropertyOwnerOnboardingReviewCountQuery'
import { usePropertyOwnerBankAccountStatusCountsQuery } from '@modules/propertyOwners/accounts/api/queries/usePropertyOwnerBankAccountStatusCountsQuery'
import { usePropertyOwnerVaspStatusCountsQuery } from '@modules/propertyOwners/accounts/api/queries/usePropertyOwnerVaspStatusCountsQuery'
import { usePropertyOwnerRejectedCountQuery } from '@modules/propertyOwners/accounts/api/queries/usePropertyOwnerRejectedCountQuery'
import {
  useOrdersPendingApprovalCountQuery,
  useVaspProcessingStatusCountsQuery,
} from '@modules/orders'
import { useVaspWalletStatusCountsQuery } from '@modules/wallets'
import logo from '@/assets/images/logo.svg'
import sidebarLogo from '@/assets/images/sidebar-logo.svg'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Image from '@uiKit/elements/Image.vue'

interface NavSubItem {
  label: string
  icon: string
  to: string | { name: string; params?: Record<string, unknown> }
  count?: number
  tag?: string
  /** When true, `to` is an absolute URL opened in a new tab (external tool, e.g. Kibana). */
  external?: boolean
}

interface NavGroup {
  label: string
  items: NavSubItem[]
}

interface NavItem {
  label: string
  icon: string
  to?: string | { name: string; params?: Record<string, unknown> }
  badge?: number
  /** Pack/owner tag rendered to the right of the label (e.g. PROP, ARVA, SRE). */
  tag?: string
  groups?: NavGroup[]
}

interface NavSection {
  /** Section header text; omit (or '') to render the items without a header. */
  label?: string
  items: NavItem[]
}

withDefaults(
  defineProps<{
    mobileOpen?: boolean
    onCloseMobile?: () => void
    collapsed?: boolean
  }>(),
  { mobileOpen: false, collapsed: false },
)

const route = useRoute()
const expandedMenus = ref<string[]>([])

// Treasury withdraw-queue badge — sum of "needs action" statuses.
// Cached query: the badge subscribes to the same key the page uses, so
// when an approve / mark-settled / reject mutation invalidates it, the
// badge refreshes without an extra fetch.
const { data: withdrawCounts } = useWithdrawRequestStatusCountsQuery()
const withdrawApprovalsBadge = computed(() => {
  const counts = withdrawCounts.value
  if (!counts) return undefined
  const total = counts.pendingApproval + counts.awaitingSettlement
  return total > 0 ? total : undefined
})

// Treasury deposit-queue badge — same pattern as withdraw. Subscribes
// to the deposit status-counts query; the approve / reject mutations
// invalidate the key so the badge stays fresh.
const { data: depositCounts } = useDepositRequestStatusCountsQuery()
const depositApprovalsBadge = computed(() => {
  const counts = depositCounts.value
  if (!counts) return undefined
  return counts.pendingVerification > 0 ? counts.pendingVerification : undefined
})

// Treasury refund-queue badge — same pattern. Pending is the only
// actionable state for the refund work queue.
const { data: refundCounts } = useRefundStatusCountsQuery()
const refundApprovalsBadge = computed(() => {
  const counts = refundCounts.value
  if (!counts) return undefined
  return counts.pending > 0 ? counts.pending : undefined
})

// Investor work-queue badges — live counts from per-queue endpoints
// (BE batch A). Each mutation invalidates the relevant key so badges
// stay fresh without an extra fetch.
const { data: kycPendingCounts } = useKycPendingStatusCountsQuery()
const kycPendingBadge = computed(() => {
  const n = kycPendingCounts.value?.pending ?? 0
  return n > 0 ? n : undefined
})

const { data: kycExpiredCounts } = useKycExpiredStatusCountsQuery()
const kycExpiredBadge = computed(() => {
  const n = kycExpiredCounts.value?.expired ?? 0
  return n > 0 ? n : undefined
})

const { data: bankVerificationCounts } = useBankVerificationStatusCountsQuery()
const bankVerificationsBadge = computed(() => {
  const n = bankVerificationCounts.value?.pendingVerification ?? 0
  return n > 0 ? n : undefined
})

const { data: vaspOnboardingCounts } = useVaspOnboardingStatusCountsQuery()
const vaspOnboardingBadge = computed(() => {
  const n = vaspOnboardingCounts.value?.pendingVaspApproval ?? 0
  return n > 0 ? n : undefined
})

const { data: rejectedInvestorsCounts } = useRejectedInvestorsStatusCountsQuery()
const rejectedInvestorsBadge = computed(() => {
  const n = rejectedInvestorsCounts.value?.rejected ?? 0
  return n > 0 ? n : undefined
})

// Onboarding-review badge — dedicated per-queue endpoint (admin-service
// commit a6dddfb) counts investors in pending_review with KYC approved
// AND onboarding completed.
const { data: onboardingReviewCounts } = useOnboardingReviewStatusCountsQuery()
const onboardingReviewBadge = computed(() => {
  const n = onboardingReviewCounts.value?.pendingReview ?? 0
  return n > 0 ? n : undefined
})

// Property queue badges — powered by the same key as PropertyListContainer.
// Mutations that change property status invalidate propertyKeys.statusCounts()
// so these refresh without an extra fetch.
const { data: propertyCounts } = usePropertyStatusCountsQuery()
const propertyVerificationBadge = computed(() => {
  const n = propertyCounts.value?.inVerification ?? 0
  return n > 0 ? n : undefined
})
const propertyComplianceBadge = computed(() => {
  const n = propertyCounts.value?.inCompliance ?? 0
  return n > 0 ? n : undefined
})
const propertyFinancialSetupBadge = computed(() => {
  const n = propertyCounts.value?.inFinancialSetup ?? 0
  return n > 0 ? n : undefined
})
const propertyVaspBadge = computed(() => {
  const n = propertyCounts.value?.inVasp ?? 0
  return n > 0 ? n : undefined
})
const propertyDraftBadge = computed(() => {
  const n = propertyCounts.value?.draft ?? 0
  return n > 0 ? n : undefined
})
const propertyAllBadge = computed(() => {
  const n = propertyCounts.value?.all ?? 0
  return n > 0 ? n : undefined
})
// Refresh queues (property-refresh-queues-plan.md §8). Surface count on the
// sidebar so the on-call PO / Fin Admin can see how many properties need
// attention at a glance.
const propertyRentalRefreshBadge = computed(() => {
  const n = propertyCounts.value?.rentalRefresh ?? 0
  return n > 0 ? n : undefined
})
const propertyValuationRefreshBadge = computed(() => {
  const n = propertyCounts.value?.valuationRefresh ?? 0
  return n > 0 ? n : undefined
})
// Rental distribution badges the runs an operator still has to move: drafts
// waiting to be submitted, and submitted runs waiting on a checker. Both are
// money that is due to investors and has not moved yet.
const { data: rentalDistributionCounts } = useRentalDistributionCountsQuery()
const rentalDistributionBadge = computed(() => {
  const counts = rentalDistributionCounts.value
  if (!counts) return undefined
  const n = counts.draft + counts.pendingApproval + counts.declared
  return n > 0 ? n : undefined
})
// Rent receipts badges the ones whose bank credit disagrees with the manager's
// statement, or that are being queried — nothing downstream can be paid out
// until a person resolves them.
const { data: rentReceiptCounts } = useRentReceiptCountsQuery()
const rentReceiptsBadge = computed(() => {
  const n = rentReceiptCounts.value?.needsAttention ?? 0
  return n > 0 ? n : undefined
})
const propertyRejectedBadge = computed(() => {
  const n = propertyCounts.value?.rejected ?? 0
  return n > 0 ? n : undefined
})
// Listing requests — the module builds its own sub-nav (labels, icons, routes,
// per-queue badges) from its queue registry, so a new lifecycle queue shows up
// here without editing this file. No parent-level badge: the directory queues
// include an unfiltered "All Requests" total, so any roll-up would double-count,
// and no other parent item in this nav carries one.
const { groups: listingRequestGroups } = useListingRequestNavGroups()

// Offering work-queue badges — powered by the same key tree as the offerings
// list queries. Every offering mutation invalidates offeringPropertyQueryKeys.all
// (the parent of statusCounts), so these badges refresh without an extra fetch.
const { data: offeringCounts } = useOfferingStatusCountsQuery()
const offeringDraftBadge = computed(() => {
  const n = offeringCounts.value?.draft ?? 0
  return n > 0 ? n : undefined
})
const offeringPendingApprovalBadge = computed(() => {
  const n = offeringCounts.value?.pendingApproval ?? 0
  return n > 0 ? n : undefined
})
const offeringVaspOnboardingBadge = computed(() => {
  const n = offeringCounts.value?.vaspOnboarding ?? 0
  return n > 0 ? n : undefined
})
const offeringLiveBadge = computed(() => {
  const n = offeringCounts.value?.live ?? 0
  return n > 0 ? n : undefined
})
const offeringFullyFundedBadge = computed(() => {
  const n = offeringCounts.value?.fullyFunded ?? 0
  return n > 0 ? n : undefined
})
const offeringRejectedBadge = computed(() => {
  const n = offeringCounts.value?.rejected ?? 0
  return n > 0 ? n : undefined
})

// Property-owner work-queue badges. Bank + VASP come from dedicated PO
// status-count endpoints; KYC review / onboarding review / rejected reuse the
// queue list endpoints (limit:1, count-only). Each subscribes to the same key
// the queue page uses, so the relevant mutation invalidation refreshes the
// badge without an extra fetch.
const { data: poKycReviewCount } = usePropertyOwnerKycReviewCountQuery()
const poKycReviewBadge = computed(() => {
  const n = poKycReviewCount.value ?? 0
  return n > 0 ? n : undefined
})

const { data: poOnboardingReviewCount } = usePropertyOwnerOnboardingReviewCountQuery()
const poOnboardingReviewBadge = computed(() => {
  const n = poOnboardingReviewCount.value ?? 0
  return n > 0 ? n : undefined
})

const { data: poBankCounts } = usePropertyOwnerBankAccountStatusCountsQuery()
const poBankVerificationsBadge = computed(() => {
  const n = poBankCounts.value?.pending ?? 0
  return n > 0 ? n : undefined
})

const { data: poVaspCounts } = usePropertyOwnerVaspStatusCountsQuery()
const poVaspOnboardingBadge = computed(() => {
  const n = poVaspCounts.value?.pendingVaspApproval ?? 0
  return n > 0 ? n : undefined
})

const { data: poRejectedCount } = usePropertyOwnerRejectedCountQuery()
const poRejectedBadge = computed(() => {
  const n = poRejectedCount.value ?? 0
  return n > 0 ? n : undefined
})

// Orders work-queue badges — live counts from the orders API.
// Both hooks place their keys under ordersQueryKeys.lists() so any mutation
// that invalidates the list tree automatically refreshes these badges.
const { data: ordersPendingApprovalCount } = useOrdersPendingApprovalCountQuery()
const ordersPendingApprovalBadge = computed(() => {
  const n = ordersPendingApprovalCount.value ?? 0
  return n > 0 ? n : undefined
})

// No poll interval: the badge subscribes app-wide, so only the queue page opts in.
const { data: vaspStatusCounts } = useVaspProcessingStatusCountsQuery()
const vaspProcessingBadge = computed(() => {
  // `all` is computed server-side over the same buckets the queue lists, so the
  // badge and the queue's ALL tab agree by construction. Re-summing here is how
  // they used to drift.
  const total = vaspStatusCounts.value?.all ?? 0
  return total > 0 ? total : undefined
})

// VAISP wallet-onboarding badge — wallets minted by the VAISP approve flow that
// Ctrl-Alt has not validated yet, i.e. the rows a sync would move. Same cached
// key the queue page uses, so a sync mutation refreshes the badge for free.
const { data: vaspWalletCounts } = useVaspWalletStatusCountsQuery()
const vaspWalletOnboardingBadge = computed(() => {
  const pending = vaspWalletCounts.value?.pendingValidation ?? 0
  return pending > 0 ? pending : undefined
})

// Canonical nav — mirrors /Users/suhailtamboli/Documents/sliceal/demo-admin/assets/layout.js
// Sub-item paths use existing admin-web routes where available; placeholder paths
// (e.g. /treasury/pending-deposits) preserve the menu structure for features that
// haven't shipped routes yet — they'll hit the NotFoundPage until wired up.
//
// `sections` is a computed so live counts (e.g. withdrawal approvals badge)
// can flow into the nav without re-mounting the sidebar.
const sections = computed<NavSection[]>(() => [
  {
    items: [{ label: 'Dashboard', to: routes.DASHBOARD, icon: 'th-large' }],
  },
  {
    label: 'Users',
    items: [
      {
        label: 'Investors',
        to: routes.INVESTOR,
        icon: 'users',
        groups: [
          {
            label: 'Work queues',
            items: [
              {
                label: 'KYC Pending',
                icon: 'clock',
                to: routes.INVESTORS_KYC_PENDING,
                count: kycPendingBadge.value,
              },
              {
                label: 'Onboarding Review',
                icon: 'list',
                to: routes.INVESTORS_ONBOARDING_REVIEW,
                count: onboardingReviewBadge.value,
              },
              {
                label: 'Bank Verifications',
                icon: 'building-columns',
                to: routes.INVESTORS_BANK_VERIFICATIONS,
                count: bankVerificationsBadge.value,
              },
              {
                label: 'VAISP Onboarding',
                icon: 'box',
                to: routes.INVESTORS_VASP_ONBOARDING,
                count: vaspOnboardingBadge.value,
              },
              {
                label: 'KYC Expired',
                icon: 'calendar-times',
                to: routes.INVESTORS_KYC_EXPIRED,
                count: kycExpiredBadge.value,
              },
            ],
          },
          {
            label: 'Directory',
            items: [
              { label: 'All Investors', icon: 'bars', to: routes.INVESTORS_ALL },
              {
                label: 'Rejected',
                icon: 'ban',
                to: routes.INVESTORS_REJECTED,
                count: rejectedInvestorsBadge.value,
              },
              { label: 'Blocked', icon: 'lock', to: routes.INVESTORS_BLOCKED },
            ],
          },
        ],
      },
      {
        label: 'Property owners',
        to: routes.PROPERTY_OWNERS,
        icon: 'building',
        groups: [
          {
            label: 'Operations',
            items: [
              {
                label: 'KYC / KYB Review',
                icon: 'id-card',
                to: routes.PROPERTY_OWNERS_KYC_REVIEW,
                count: poKycReviewBadge.value,
              },
              {
                label: 'Onboarding Review',
                icon: 'clock',
                to: routes.PROPERTY_OWNERS_PENDING,
                count: poOnboardingReviewBadge.value,
              },
              {
                label: 'Bank Verifications',
                icon: 'building-columns',
                to: routes.PROPERTY_OWNERS_BANK_VERIFICATIONS,
                count: poBankVerificationsBadge.value,
              },
              {
                label: 'VAISP Onboarding',
                icon: 'box',
                to: routes.PROPERTY_OWNERS_VASP,
                count: poVaspOnboardingBadge.value,
              },
            ],
          },
          {
            label: 'Directory',
            items: [
              { label: 'Create Owner', icon: 'plus', to: routes.PROPERTY_OWNERS_CREATE },
              { label: 'Draft Owners', icon: 'pencil', to: routes.PROPERTY_OWNERS_DRAFT },
              { label: 'All property owners', icon: 'bars', to: routes.PROPERTY_OWNERS_ALL },
              {
                label: 'Bank Accounts',
                icon: 'building-columns',
                to: routes.PROPERTY_OWNERS_BANK_ACCOUNTS,
              },
              {
                label: 'Rejected',
                icon: 'times-circle',
                to: routes.PROPERTY_OWNERS_REJECTED,
                count: poRejectedBadge.value,
              },
              { label: 'Blocked', icon: 'ban', to: routes.PROPERTY_OWNERS_BLOCKED },
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'Assets',
    items: [
      {
        // Seller listing lifecycle — sits before Properties because a request
        // becomes a property, not the other way round. Groups + badges come
        // from the module's queue registry (see useListingRequestNavGroups).
        label: 'Listing Requests',
        to: routes.LISTING_REQUESTS,
        icon: 'inbox',
        groups: listingRequestGroups.value,
      },
      {
        label: 'Properties',
        to: routes.PROPERTIES,
        icon: 'home',
        groups: [
          {
            label: 'Onboarding queues',
            items: [
              {
                label: 'Ops Enrichment',
                icon: 'pencil',
                to: routes.PROPERTIES_ENRICHMENT,
              },
              {
                label: 'Financial Setup',
                icon: 'percentage',
                to: routes.PROPERTIES_FINANCIAL_SETUP,
                count: propertyFinancialSetupBadge.value,
              },
              {
                label: 'Property Verification',
                icon: 'list',
                to: routes.PROPERTIES_VERIFICATION,
                count: propertyVerificationBadge.value,
              },
              {
                label: 'Compliance Review',
                icon: 'shield',
                to: routes.PROPERTIES_COMPLIANCE,
                count: propertyComplianceBadge.value,
              },
              {
                label: 'VAISP Onboarding',
                icon: 'box',
                to: routes.PROPERTIES_VASP,
                count: propertyVaspBadge.value,
              },
            ],
          },
          {
            // Post-approval queues (property-refresh-queues-plan.md §1):
            // these surface attention items on properties that are ALREADY
            // approved — never gate status transitions. Kept in their own
            // group so the operator doesn't confuse them with onboarding.
            label: 'Post-approval queues',
            items: [
              {
                label: 'Rental Refresh',
                icon: 'calendar',
                to: routes.PROPERTIES_RENTAL_REFRESH,
                count: propertyRentalRefreshBadge.value,
              },
              {
                label: 'Valuation Refresh',
                icon: 'chart-line',
                to: routes.PROPERTIES_VALUATION_REFRESH,
                count: propertyValuationRefreshBadge.value,
              },
            ],
          },
          {
            label: 'Directory',
            items: [
              { label: 'Create Property', icon: 'plus', to: routes.ADD_PROPERTY },
              {
                label: 'Draft Properties',
                icon: 'pencil',
                to: routes.PROPERTIES_DRAFT,
                count: propertyDraftBadge.value,
              },
              { label: 'Onboarding Pipeline', icon: 'chart-bar', to: routes.PROPERTIES_PIPELINE },
              {
                label: 'All Properties',
                icon: 'bars',
                to: routes.PROPERTIES,
                count: propertyAllBadge.value,
              },
              {
                label: 'Rejected',
                icon: 'ban',
                to: routes.PROPERTIES_REJECTED,
                count: propertyRejectedBadge.value,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'Sales',
    items: [
      {
        label: 'Offerings',
        to: routes.OFFERINGS,
        icon: 'ticket',
        groups: [
          {
            label: 'Work queues',
            items: [
              { label: 'Create Offering', icon: 'plus', to: routes.OFFERING_CONFIGURE },
              {
                label: 'Drafts',
                icon: 'pencil',
                to: routes.OFFERINGS_DRAFTS,
                count: offeringDraftBadge.value,
              },
              {
                label: 'Pending Approval',
                icon: 'list',
                to: routes.OFFERINGS_PENDING_APPROVAL,
                count: offeringPendingApprovalBadge.value,
              },
              {
                label: 'VAISP Onboarding',
                icon: 'box',
                to: routes.OFFERINGS_VASP_ONBOARDING,
                count: offeringVaspOnboardingBadge.value,
              },
              {
                label: 'Live Offerings',
                icon: 'wifi',
                to: routes.OFFERINGS_LIVE,
                count: offeringLiveBadge.value,
              },
              {
                label: 'Fully Funded',
                icon: 'check-circle',
                to: routes.OFFERINGS_FULLY_FUNDED,
                count: offeringFullyFundedBadge.value,
              },
            ],
          },
          {
            label: 'Directory',
            items: [
              { label: 'All Offerings', icon: 'bars', to: routes.OFFERINGS_ALL },
              {
                label: 'Rejected Offerings',
                icon: 'ban',
                to: routes.OFFERINGS_REJECTED,
                count: offeringRejectedBadge.value,
              },
            ],
          },
        ],
      },
      {
        label: 'Orders',
        to: routes.ORDERS,
        icon: 'arrow-right-arrow-left',
        groups: [
          {
            label: 'Work queues',
            items: [
              {
                label: 'Pending approval',
                icon: 'list',
                to: routes.ORDERS_PENDING_APPROVAL,
                count: ordersPendingApprovalBadge.value,
              },
              {
                label: 'VAISP processing',
                icon: 'box',
                to: routes.ORDERS_VASP_PROCESSING,
                count: vaspProcessingBadge.value,
              },
            ],
          },
          {
            label: 'Directory',
            items: [
              { label: 'All orders', icon: 'bars', to: routes.ORDERS_ALL },
              { label: 'Completed', icon: 'check-circle', to: routes.ORDERS_COMPLETED },
              { label: 'Rejected', icon: 'times-circle', to: routes.ORDERS_REJECTED },
              { label: 'Refund', icon: 'refresh', to: routes.ORDERS_REFUND },
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'Money',
    items: [
      {
        label: 'Treasury',
        to: '/treasury',
        icon: 'dollar',
        tag: 'PROP',
        groups: [
          {
            label: 'Work queues',
            items: [
              {
                label: 'Pending deposits',
                icon: 'hourglass',
                to: routes.TREASURY_DEPOSIT_REQUESTS,
                count: depositApprovalsBadge.value,
              },
              {
                label: 'Withdrawal approvals',
                icon: 'list',
                to: routes.TREASURY_WITHDRAW_REQUESTS,
                count: withdrawApprovalsBadge.value,
              },
              {
                label: 'Refund queue',
                icon: 'replay',
                to: routes.TREASURY_REFUND_REQUESTS,
                count: refundApprovalsBadge.value,
              },
            ],
          },
          {
            label: 'Directory',
            items: [
              {
                label: 'Wallets',
                icon: 'wallet',
                to: routes.TREASURY_WALLETS_DIRECTORY,
              },
              {
                label: 'Bank accounts',
                icon: 'building-columns',
                to: routes.TREASURY_BANK_ACCOUNTS_DIRECTORY,
              },
              {
                label: 'Treasury accounts',
                icon: 'briefcase',
                to: routes.TREASURY_ACCOUNTS_DIRECTORY,
              },
              {
                label: 'All transactions',
                icon: 'bars',
                to: routes.TREASURY_FIAT_TRANSACTIONS_LIST,
              },
            ],
          },
        ],
      },
      {
        // A money module, not a property one: approving a run credits investor
        // fiat wallets. It sits beside Treasury for that reason.
        label: 'Rental Distribution',
        to: '/rental-distributions',
        icon: 'home',
        groups: [
          {
            label: 'Work queues',
            items: [
              {
                label: 'Rent receipts',
                icon: 'inbox',
                to: routes.RENTAL_DISTRIBUTION_RECEIPTS,
                count: rentReceiptsBadge.value,
              },
              {
                label: 'Distribution runs',
                icon: 'wallet',
                to: routes.RENTAL_DISTRIBUTION_RUNS,
                count: rentalDistributionBadge.value,
              },
              {
                label: 'Property rent ledger',
                icon: 'book',
                to: routes.RENTAL_DISTRIBUTION_LEDGER,
              },
              {
                label: 'Investor payouts',
                icon: 'users',
                to: routes.RENTAL_DISTRIBUTION_PAYOUTS,
              },
            ],
          },
        ],
      },
      {
        label: 'Crypto wallets',
        to: '/wallets',
        icon: 'wallet',
        tag: 'ARVA',
        groups: [
          {
            label: 'Work queues',
            items: [
              {
                label: 'VAISP onboarding',
                icon: 'id-card',
                to: '/wallets/crypto-wallets/vasp-onboarding',
                count: vaspWalletOnboardingBadge.value,
              },
              {
                label: 'Wallet management',
                icon: 'wallet',
                to: '/wallets/crypto-wallets/management',
              },
              // Both are the wallet management table under a filter, sharing its
              // page component but keeping their own paths — active highlighting
              // here is `route.path.startsWith(item.to)`, so a query string would
              // leave all three sharing one path and none of them lit.
              //
              // They previously pointed at /wallets/trustline-approvals and
              // /wallets/failed-onchain, neither of which was ever registered as a
              // route — the links rendered but resolved to nothing. Their counts
              // were hardcoded literals (6 and 2), so the badges asserted work
              // that may not exist; dropped rather than faked.
              {
                label: 'Trustline approvals',
                icon: 'link',
                to: '/wallets/crypto-wallets/trustline-approvals',
              },
              {
                label: 'Failed on-chain ops',
                icon: 'exclamation-triangle',
                to: '/wallets/crypto-wallets/failed-onchain',
              },
            ],
          },
          {
            label: 'Directory',
            items: [
              { label: 'All wallets', icon: 'bars', to: '/wallets/all' },
              {
                label: 'On-chain transactions',
                icon: 'arrow-right-arrow-left',
                to: '/wallets/transactions',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'Platform',
    items: [
      {
        label: 'Monitoring',
        to: '/monitoring',
        icon: 'chart-line',
        tag: 'SRE',
        groups: [
          {
            label: 'System health',
            items: [
              // "Infra dashboard" opens the external Kibana executive dashboard when a
              // per-env URL is configured (config.monitoring); otherwise the internal route.
              config.monitoring.kibanaExecutiveDashboardUrl
                ? {
                    label: 'Infra dashboard',
                    icon: 'server',
                    to: config.monitoring.kibanaExecutiveDashboardUrl,
                    external: true,
                  }
                : { label: 'Infra dashboard', icon: 'server', to: '/monitoring/infra' },
              // "APM" / "Logs" open the external Kibana views when a per-env URL is
              // configured (config.monitoring); otherwise the internal route.
              config.monitoring.kibanaApmUrl
                ? {
                    label: 'APM',
                    icon: 'stopwatch',
                    to: config.monitoring.kibanaApmUrl,
                    external: true,
                  }
                : { label: 'APM', icon: 'stopwatch', to: '/monitoring/apm' },
              config.monitoring.kibanaLogsUrl
                ? {
                    label: 'Logs',
                    icon: 'file',
                    to: config.monitoring.kibanaLogsUrl,
                    external: true,
                  }
                : { label: 'Logs', icon: 'file', to: '/monitoring/logs' },
              {
                label: 'Integration health',
                icon: 'link',
                to: '/monitoring/integrations',
              },
            ],
          },
          {
            label: 'Security',
            items: [{ label: 'Login history', icon: 'lock', to: '/monitoring/login-history' }],
          },
        ],
      },
      {
        label: 'Settings',
        to: routes.SETTINGS,
        icon: 'cog',
        tag: 'PROP',
        groups: [
          {
            label: 'Team & access',
            items: [
              { label: 'Admin users', icon: 'user', to: routes.ADMINS },
              {
                label: 'Roles & permissions',
                icon: 'key',
                to: routes.SETTINGS_ROLES,
              },
              {
                label: 'Admin activity log',
                icon: 'history',
                to: '/settings/admin-activity',
              },
            ],
          },
          {
            label: 'Platform',
            items: [
              {
                label: 'Country configurations',
                icon: 'globe',
                to: '/settings/countries',
              },
              {
                label: 'Fee configuration',
                icon: 'percentage',
                to: routes.SETTINGS_FEE_CONFIGURATION,
              },
              {
                label: 'Limits & thresholds',
                icon: 'chart-bar',
                to: '/settings/limits',
              },
              {
                label: 'Notification templates',
                icon: 'envelope',
                to: '/settings/templates',
              },
              {
                label: 'Agreement templates',
                icon: 'file-edit',
                to: routes.AGREEMENT_TEMPLATES,
              },
            ],
          },
          {
            label: 'Integrations',
            items: [
              { label: 'Sumsub', icon: 'id-card', to: '/settings/sumsub' },
              { label: 'VAISP (Ctrl Alt)', icon: 'box', to: '/settings/ctrlalt' },
              { label: 'CMA bank', icon: 'building-columns', to: '/settings/cma' },
              { label: 'XRPL / Arva', icon: 'wallet', to: '/settings/xrpl' },
              { label: 'DLD', icon: 'building', to: '/settings/dld' },
              { label: 'API keys & webhooks', icon: 'cloud', to: '/settings/apikeys' },
            ],
          },
        ],
      },
    ],
  },
])

const allItems = computed<NavItem[]>(() => sections.value.flatMap((s) => s.items))

const subItemMatchesRoute = (item: NavItem): boolean => {
  if (!item.groups) return false
  return item.groups.some((g) =>
    g.items.some((s) => {
      if (typeof s.to === 'string') return route.path === s.to || route.path.startsWith(s.to)
      if (typeof s.to === 'object' && 'name' in s.to) return route.name === s.to.name
      return false
    }),
  )
}

onMounted(() => {
  allItems.value.forEach((item) => {
    if (!item.groups) return
    const toPath = typeof item.to === 'string' ? item.to : null
    if ((toPath && route.path.startsWith(toPath)) || subItemMatchesRoute(item)) {
      expandedMenus.value.push(item.label)
    }
  })
})

const toggleMenu = (label: string) => {
  const idx = expandedMenus.value.indexOf(label)
  if (idx > -1) expandedMenus.value.splice(idx, 1)
  else expandedMenus.value.push(label)
}

const isExpanded = (label: string) => expandedMenus.value.includes(label)

const isItemActive = (item: NavItem) => {
  if (!item.to) return false
  const p = route.path
  if (typeof item.to === 'string') return p.startsWith(item.to) && item.to !== '/'
  if (typeof item.to === 'object' && 'name' in item.to) return route.name === item.to.name
  return false
}

// Every sub-item path in the sidebar, used to resolve "most specific wins" below.
const allSubItemPaths = computed<string[]>(() =>
  sections.value
    .flatMap((section) => section.items)
    .flatMap((item) => item.groups ?? [])
    .flatMap((group) => group.items)
    .map((subItem) => (typeof subItem.to === 'string' ? subItem.to : ''))
    .filter(Boolean),
)

/**
 * Exact match, or a prefix match on a `/` boundary when no deeper sub-item claims
 * the route.
 *
 * Exact-only left every detail page with NO sub-item lit: on
 * /wallets/crypto-wallets/management/<investorId> nothing matched, while the
 * parent item (which uses startsWith) did — so opening an investor from a work
 * queue collapsed the highlight up to "Crypto wallets" and the operator lost
 * their place.
 *
 * The specificity check is what keeps the prefix rule honest: without it a
 * detail route would light every ancestor sub-item that happens to share a
 * prefix, which is a different wrong answer.
 */
const isSubActive = (to: NavSubItem['to']) => {
  if (typeof to === 'object' && 'name' in to) return route.name === to.name
  if (typeof to !== 'string') return false
  if (route.path === to) return true
  if (!route.path.startsWith(`${to}/`)) return false

  return !allSubItemPaths.value.some(
    (other) =>
      other.length > to.length && (route.path === other || route.path.startsWith(`${other}/`)),
  )
}

// ── Accordion height-auto transition ──────────────────────────────────
// Animates open/close on the children list. Uses JS hooks because
// CSS-only height transitions don't work on `auto`. The motion ease + base
// duration come from the kit's `--p-sliceal-motion-*` tokens via the
// scoped CSS variables below.
function onAccordionBeforeEnter(el: Element) {
  ;(el as HTMLElement).style.height = '0'
  ;(el as HTMLElement).style.opacity = '0'
}
function onAccordionEnter(el: Element, done: () => void) {
  const target = el as HTMLElement
  // Force layout, then animate to natural height.
  const naturalHeight = target.scrollHeight
  requestAnimationFrame(() => {
    target.style.height = `${naturalHeight}px`
    target.style.opacity = '1'
    target.addEventListener('transitionend', function handler() {
      target.removeEventListener('transitionend', handler)
      target.style.height = 'auto'
      done()
    })
  })
}
function onAccordionLeave(el: Element, done: () => void) {
  const target = el as HTMLElement
  target.style.height = `${target.scrollHeight}px`
  // Allow the height to commit before transitioning to 0.
  requestAnimationFrame(() => {
    target.style.height = '0'
    target.style.opacity = '0'
    target.addEventListener('transitionend', function handler() {
      target.removeEventListener('transitionend', handler)
      done()
    })
  })
}
</script>

<template>
  <Box
    is="aside"
    class="sidebar"
    :class="{
      'sidebar--mobile-open': mobileOpen,
      'sidebar--collapsed': collapsed,
    }"
  >
    <!-- Brand -->
    <Box class="sidebar-header">
      <Box class="brand">
        <!-- Collapsed rail shows the icon-only mark; the expanded sidebar
             shows the full wordmark logo. Both are rendered and toggled via
             CSS so the mobile drawer (which re-expands a collapsed sidebar)
             always gets the full logo. -->
        <Image :src="logo" alt="Sliceal" class="brand-icon" />
        <Image :src="sidebarLogo" alt="Sliceal" class="brand-logo" />
      </Box>
      <Box
        is="button"
        class="mobile-close"
        type="button"
        aria-label="Close sidebar"
        @click="onCloseMobile"
      >
        <PiIcon name="times" />
      </Box>
    </Box>

    <!-- Nav content -->
    <Box class="sidebar-content custom-scrollbar">
      <Box
        v-for="(section, sIdx) in sections"
        :key="section.label ?? `_section_${sIdx}`"
        class="nav-section"
      >
        <Text v-if="section.label" is="span" type="caption" class="section-title">{{
          section.label
        }}</Text>
        <Box v-for="item in section.items" :key="item.label" class="nav-item-wrap">
          <!-- Group with sub-items -->
          <Box v-if="item.groups">
            <Box
              is="button"
              class="nav-item nav-item--accordion"
              :class="{ 'nav-item--active': isItemActive(item) }"
              :title="collapsed ? item.label : undefined"
              @click="toggleMenu(item.label)"
            >
              <Box is="span" class="nav-icon-wrap">
                <PiIcon :name="item.icon" class="nav-icon" />
              </Box>
              <Text is="span" type="body" class="nav-label">{{ item.label }}</Text>
              <Text v-if="item.tag && !collapsed" is="span" type="caption" class="pack-tag">{{
                item.tag
              }}</Text>
              <Text
                v-else-if="item.badge && !collapsed"
                is="span"
                type="caption"
                class="badge-red"
                >{{ item.badge }}</Text
              >
              <PiIcon
                name="chevron-down"
                class="chevron"
                :class="{ 'chevron--up': isExpanded(item.label) }"
              />
            </Box>

            <Transition
              name="accordion"
              @before-enter="onAccordionBeforeEnter"
              @enter="onAccordionEnter"
              @leave="onAccordionLeave"
            >
              <Box v-if="isExpanded(item.label) && !collapsed" class="accordion-body">
                <Box v-for="group in item.groups" :key="group.label" class="sub-group">
                  <Text is="span" type="caption" class="sub-group-label">{{ group.label }}</Text>
                  <component
                    :is="sub.external ? 'a' : RouterLink"
                    v-for="sub in group.items"
                    :key="sub.label"
                    :to="sub.external ? undefined : sub.to"
                    :href="sub.external ? sub.to : undefined"
                    :target="sub.external ? '_blank' : undefined"
                    :rel="sub.external ? 'noopener noreferrer' : undefined"
                    class="sub-item"
                    :class="{ 'sub-item--active': !sub.external && isSubActive(sub.to) }"
                  >
                    <Box is="span" class="sub-icon-wrap">
                      <PiIcon :name="sub.icon" class="sub-icon" />
                    </Box>
                    <Text is="span" type="body" class="sub-label">{{ sub.label }}</Text>
                    <Text v-if="sub.count" is="span" type="caption" class="badge-count">{{
                      sub.count
                    }}</Text>
                    <Text v-else-if="sub.tag" is="span" type="caption" class="badge-tag">{{
                      sub.tag
                    }}</Text>
                    <PiIcon v-if="sub.external" name="external-link" class="sub-external-icon" />
                  </component>
                </Box>
              </Box>
            </Transition>
          </Box>

          <!-- Flat link item -->
          <RouterLink
            v-else-if="item.to"
            :to="item.to"
            class="nav-item"
            :class="{ 'nav-item--active': isItemActive(item) }"
            :title="collapsed ? item.label : undefined"
          >
            <Box is="span" class="nav-icon-wrap">
              <PiIcon :name="item.icon" class="nav-icon" />
            </Box>
            <Text is="span" type="body" class="nav-label">{{ item.label }}</Text>
            <Text v-if="item.tag && !collapsed" is="span" type="caption" class="pack-tag">{{
              item.tag
            }}</Text>
            <Text v-else-if="item.badge && !collapsed" is="span" type="caption" class="badge-red">{{
              item.badge
            }}</Text>
          </RouterLink>
        </Box>
      </Box>
    </Box>
  </Box>
</template>

<style scoped>
/* ── Base ──
   Dark institutional surface — always dark regardless of app theme. The
   sidebar reads as the operator's command-line chrome (Stripe / Ramp /
   Mercury ops console). Palette lives on `--p-sliceal-sidebar-*` tokens.
*/
.sidebar {
  /* Local aliases for the kit's motion tokens so every transition in
     this file shares the same rhythm. */
  --motion-base: var(--p-sliceal-motion-base, 240ms);
  --motion-fast: var(--p-sliceal-motion-fast, 160ms);
  --motion-ease: var(--p-sliceal-motion-ease, cubic-bezier(0.22, 1, 0.36, 1));

  width: 248px;
  height: 100vh;
  /* Layered midnight-indigo gradient. Premium fintech-OS surface — deep
     indigo at the top fading through slate-indigo into near-black. */
  background: linear-gradient(
    180deg,
    var(--p-sliceal-sidebar-gradient-start) 0%,
    var(--p-sliceal-sidebar-gradient-high) 42%,
    var(--p-sliceal-sidebar-gradient-low) 72%,
    var(--p-sliceal-sidebar-gradient-end) 100%
  );
  color: var(--p-sliceal-sidebar-text);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  overflow: hidden;
  transition: width var(--motion-base) var(--motion-ease);
  z-index: 101;
  border-right: 1px solid var(--p-sliceal-sidebar-border);
  box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.02);
}

/* Force kit Text descendants to inherit the dark-surface colour. Text's
   own scoped CSS sets `color: var(--p-text-color)` (dark) which would
   render labels nearly invisible against the sidebar bg. */
.sidebar :deep([class*='text--']) {
  color: inherit;
}

/* ── Header ── */
.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 1rem;
  flex-shrink: 0;
  border-bottom: 1px solid var(--p-sliceal-sidebar-border);
}

.brand {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  overflow: hidden;
}

/* Icon-only mark — hidden by default; only the collapsed rail shows it. */
.brand-icon {
  display: none;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

/* Full wordmark logo — the default (expanded) brand mark. */
.brand-logo {
  height: 22px;
  width: auto;
  display: block;
  flex-shrink: 0;
}

.mobile-close {
  display: none;
  padding: 6px;
  border: none;
  background: transparent;
  color: var(--p-sliceal-sidebar-text-muted);
  cursor: pointer;
  border-radius: 6px;
}

/* ── Scrollable content ── */
.sidebar-content {
  flex: 1;
  padding: 0.75rem 0.625rem 0;
  overflow-y: auto;
  overflow-x: hidden;
}

/* ── Section grouping ──
   Generous space between sections so the operator's eye groups them as
   distinct domain clusters rather than a flat menu.
*/
.nav-section {
  margin-top: 0;
}

.nav-section + .nav-section {
  margin-top: 1.25rem; /* ~20px */
}

.section-title {
  display: block;
  padding: 0 12px 6px;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--p-sliceal-sidebar-text-muted);
  opacity: 0.5;
  white-space: nowrap;
  overflow: hidden;
}

/* ── Nav items ── */
.nav-item-wrap {
  margin-bottom: 2px;
}

/* Strict left-grid: 12px edge → icon · 18px icon box · 12px gap · text.
   Tighter 38px row keeps the nav calm without feeling cramped.
*/
.nav-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  text-decoration: none;
  color: var(--p-sliceal-sidebar-text);
  font-size: 13.5px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
  width: 100%;
  border: none;
  background: transparent;
  font-family: inherit;
  text-align: left;
  transition:
    background var(--motion-fast) var(--motion-ease),
    color var(--motion-fast) var(--motion-ease),
    box-shadow var(--motion-fast) var(--motion-ease);
}

.nav-item:hover {
  background: var(--p-sliceal-sidebar-hover);
  color: var(--p-sliceal-sidebar-active-text);
}

/* Active state — Option 2 indigo gradient wash + thin left rail.
   Reads as "your page" with depth, not as a filled enterprise card. */
.nav-item--active {
  background: linear-gradient(
    90deg,
    rgba(124, 92, 255, 0.16) 0%,
    rgba(124, 92, 255, 0.08) 100%
  ) !important;
  color: var(--p-sliceal-sidebar-active-text) !important;
  font-weight: 600;
  box-shadow: inset 0 0 0 1px rgba(124, 92, 255, 0.12);
}

.nav-item--active::before {
  content: '';
  position: absolute;
  left: -12px;
  top: 8px;
  bottom: 8px;
  width: 2px;
  border-radius: 0 2px 2px 0;
  background: var(--p-sliceal-sidebar-accent);
}

.nav-icon-wrap {
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.nav-icon {
  font-size: 16px;
  color: var(--p-sliceal-sidebar-icon);
  transition: color var(--motion-fast) var(--motion-ease);
}

.nav-item:hover .nav-icon,
.nav-item--active .nav-icon {
  color: var(--p-sliceal-sidebar-active-text);
}

.nav-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Parent-count badge ──
   Muted institutional count chip — not a bright red consumer dot. The
   number itself communicates urgency; the chrome stays neutral.
*/
.badge-red {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  background: var(--p-sliceal-sidebar-surface);
  color: var(--p-sliceal-sidebar-active-text);
  font-size: 10.5px;
  font-weight: 600;
  border-radius: 20px;
  flex-shrink: 0;
  line-height: 1;
  border: 1px solid var(--p-sliceal-sidebar-border);
}

/* ── Pack tag (PROP / ARVA / SRE) ── */
.pack-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  padding: 0 6px;
  background: transparent;
  color: var(--p-sliceal-sidebar-text-muted);
  font-size: 9.5px;
  font-weight: 600;
  border-radius: 4px;
  letter-spacing: 0.08em;
  flex-shrink: 0;
  border: 1px solid var(--p-sliceal-sidebar-border);
}

/* ── Chevron — supportive, not primary ── */
.chevron {
  font-size: 11px;
  color: var(--p-sliceal-sidebar-icon);
  flex-shrink: 0;
  margin-left: auto;
  transition:
    transform var(--motion-base) var(--motion-ease),
    opacity var(--motion-fast) var(--motion-ease);
  opacity: 0.65;
}
.nav-item:hover .chevron,
.nav-item--active .chevron {
  opacity: 1;
}
.chevron--up {
  transform: rotate(180deg);
}

/* ── Accordion body ── */
.accordion-body {
  margin: 4px 0 6px 0;
  overflow: hidden;
}

/* ── Accordion transition ──
   JS hooks animate height; CSS handles opacity + the actual easing. The
   240ms cubic-bezier(0.22, 1, 0.36, 1) ease is the kit's "institutional"
   rhythm — exposed via `--p-sliceal-motion-*` so every animated surface
   matches.
*/
.accordion-enter-active,
.accordion-leave-active {
  transition:
    height var(--motion-base) var(--motion-ease),
    opacity var(--motion-base) var(--motion-ease);
}
.accordion-enter-from,
.accordion-leave-to {
  height: 0;
  opacity: 0;
}

/* ── Sub-group ── */
.sub-group {
  margin-top: 6px;
}

.sub-group-label {
  display: block;
  padding: 4px 12px 4px 28px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--p-sliceal-sidebar-text-muted);
  opacity: 0.5;
  white-space: nowrap;
}

/* ── Sub-items ──
   28px left indent — hierarchical but not exiled. 32px row keeps the
   accordion compact so 5–8 children stay within a comfortable viewport
   span when expanded.
*/
.sub-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 32px;
  padding: 0 10px 0 28px;
  border-radius: 7px;
  text-decoration: none;
  color: var(--p-sliceal-sidebar-text);
  font-size: 12.5px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  transition:
    background var(--motion-fast) var(--motion-ease),
    color var(--motion-fast) var(--motion-ease);
  margin-bottom: 1px;
  opacity: 0.78;
}

.sub-item:hover {
  background: var(--p-sliceal-sidebar-hover);
  color: var(--p-sliceal-sidebar-active-text);
  opacity: 1;
}

.sub-item--active {
  background: rgba(129, 140, 248, 0.06);
  color: var(--p-sliceal-sidebar-active-text);
  font-weight: 500;
  opacity: 1;
}

.sub-item--active::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--p-sliceal-sidebar-accent);
}

.sub-icon-wrap {
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sub-icon {
  font-size: 13px;
  color: var(--p-sliceal-sidebar-icon);
  opacity: 0.85;
  transition: color 0.14s ease;
}

.sub-item:hover .sub-icon,
.sub-item--active .sub-icon {
  color: var(--p-sliceal-sidebar-active-text);
}

.sub-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── External-link indicator (sub-item) ──
   Marks a sub-item that opens an external tool (e.g. Kibana) in a new tab. */
.sub-external-icon {
  flex-shrink: 0;
  margin-left: auto;
  font-size: 12px;
  opacity: 0.55;
}

/* ── Count badge (sub-item) ──
   Tonal translucent pill — the number speaks for itself. No bright fills.
*/
.badge-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 20px;
  padding: 0 7px;
  background: rgba(255, 255, 255, 0.06);
  color: var(--p-sliceal-sidebar-text);
  font-size: 11px;
  font-weight: 500;
  border-radius: 999px;
  flex-shrink: 0;
  margin-left: auto;
}

.sub-item--active .badge-count {
  background: rgba(122, 107, 255, 0.22);
  color: var(--p-sliceal-sidebar-active-text);
}

/* ── NEW tag badge (sub-item) ── */
.badge-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  padding: 0 7px;
  background: transparent;
  color: var(--p-sliceal-sidebar-accent);
  font-size: 10px;
  font-weight: 600;
  border-radius: 20px;
  flex-shrink: 0;
  margin-left: auto;
  letter-spacing: 0.06em;
  border: 1px solid var(--p-sliceal-sidebar-accent);
}

/* ── Scrollbar ── */
.custom-scrollbar::-webkit-scrollbar {
  width: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: var(--p-sliceal-sidebar-border);
  border-radius: 2px;
}

/* ── Collapsed (desktop) ── */
.sidebar--collapsed {
  width: 64px;
}

.sidebar--collapsed .sidebar-header {
  justify-content: center;
  padding: 0;
}
.sidebar--collapsed .brand {
  justify-content: center;
}
.sidebar--collapsed .brand-logo {
  display: none;
}
.sidebar--collapsed .brand-icon {
  display: block;
}
.sidebar--collapsed .section-title {
  display: none;
}

.sidebar--collapsed .nav-item-wrap {
  margin-bottom: 4px;
}

.sidebar--collapsed .nav-item {
  width: 40px;
  height: 40px;
  margin: 0 auto;
  border-radius: 8px;
  padding: 0;
  justify-content: center;
  gap: 0;
}
.sidebar--collapsed .nav-label,
.sidebar--collapsed .chevron,
.sidebar--collapsed .badge-red,
.sidebar--collapsed .pack-tag {
  display: none;
}
.sidebar--collapsed .accordion-body {
  display: none;
}
/* ── Mobile ── */
@media (max-width: 991px) {
  .sidebar {
    position: fixed;
    left: -260px;
    width: 260px !important;
  }
  .sidebar--mobile-open {
    left: 0;
  }
  .mobile-close {
    display: flex;
  }

  .sidebar--collapsed .sidebar-header {
    justify-content: space-between;
    padding: 0 1rem;
  }
  .sidebar--collapsed .brand {
    justify-content: flex-start;
  }
  .sidebar--collapsed .nav-label,
  .sidebar--collapsed .section-title,
  .sidebar--collapsed .chevron,
  .sidebar--collapsed .badge-red,
  .sidebar--collapsed .pack-tag {
    display: initial;
  }
  /* Mobile drawer re-expands a collapsed sidebar — always the full logo. */
  .sidebar--collapsed .brand-logo {
    display: block;
  }
  .sidebar--collapsed .brand-icon {
    display: none;
  }
  .sidebar--collapsed .nav-item-wrap {
    margin-bottom: 1px;
  }
  .sidebar--collapsed .nav-item {
    width: 100%;
    height: 38px;
    margin: 0;
    justify-content: flex-start;
    gap: 0.625rem;
    padding: 0 0.625rem;
  }
}
</style>
